<template>
  <div class="headerDesktop">
    <router-link :to="{ name: PAGE_NAME.HOME }">
      <div
        :class="['headerDesktop_logo', `-${projectId}`]"
        :style="{
          backgroundImage: `url(${logo.src})`,
          maskImage: `url(${logo.src})`
        }"
      />
    </router-link>

    <menu-top />

    <div class="headerDesktop_userBlock" @click.stop="toggleMenu">
      <div class="headerDesktop_userWrapper">
        <div class="headerDesktop_avatar">
          <img :src="user.avatar" alt="" class="headerDesktop_photo">
          <span v-if="isIndicatorVisible" class="headerDesktop_indicator -avatar" />
        </div>
        <div class="headerDesktop_user">
          <span class="headerDesktop_name">{{ username }}</span>
          <points-label v-if="userScoreAmount > 0" :points="userScoreAmount" only-text />
          <div v-else class="headerDesktop_email">{{ email }}</div>
        </div>
      </div>
      <div class="headerDesktop_setting" :class="{ '-open': menuOpen }" />
      <div class="headerDesktop_menu" v-if="menuOpen">
        <a
          v-if="user.isAdmin"
          class="headerDesktop_item"
          :href="urlAdmin"
          data-analytics
          data-analytics-element-type="menu_user"
          :data-analytics-element-target="urlAdmin"
          data-analytics-element-id="dashboard"
          :data-analytics-element-name="$t('dashboard')"
        >
          <i class="headerDesktop_itemIcon -ext"></i>
          <div class="headerDesktop_itemName">
            {{$t('dashboard')}}
          </div>
        </a>

        <router-link
          :to="{ name: PAGE_NAME.EDIT_PROFILE }"
          class="headerDesktop_item"
          data-analytics
          data-analytics-element-type="menu_user"
          :data-analytics-element-target="{ name: PAGE_NAME.EDIT_PROFILE }"
          :data-analytics-element-id="PAGE_NAME.EDIT_PROFILE"
          :data-analytics-element-name="$t('editProfile')"
        >
          <i class="headerDesktop_itemIcon -userEdit"></i>
          <div class="headerDesktop_itemName">
            {{ $t('editProfile') }}
          </div>
        </router-link>

        <router-link
          :to="{ name: PAGE_NAME.POINTS }"
          class="headerDesktop_item"
          data-analytics
          data-analytics-element-type="menu_user"
          :data-analytics-element-target="{ name: PAGE_NAME.POINTS }"
          :data-analytics-element-id="PAGE_NAME.POINTS"
          :data-analytics-element-name="$t('points')"
        >
          <i class="headerDesktop_itemIcon -points"></i>
          <div class="headerDesktop_itemName">
            {{ $t('Points') }} {{  projectName }}
          </div>
        </router-link>

        <router-link
          v-if="isShowVideoConsultations"
          :to="{ name: PAGE_NAME.VIDEO_CONSULTATIONS }"
          class="headerDesktop_item"
          data-analytics
          data-analytics-element-type="menu_user"
          :data-analytics-element-target="{ name: PAGE_NAME.VIDEO_CONSULTATIONS }"
          :data-analytics-element-id="PAGE_NAME.VIDEO_CONSULTATIONS"
          :data-analytics-element-name="$t('videoConsultations.VideoConsultations')"
        >
          <i class="headerDesktop_itemIcon -videoconsultations"></i>
          <div class="headerDesktop_itemName">
            {{ $t('videoConsultations.VideoConsultations') }}
          </div>
        </router-link>

        <router-link
          :to="{ name: PAGE_NAME.SETTINGS }"
          class="headerDesktop_item"
          data-analytics
          data-analytics-element-type="menu_user"
          :data-analytics-element-target="{ name: PAGE_NAME.SETTINGS }"
          :data-analytics-element-id="PAGE_NAME.SETTINGS"
          :data-analytics-element-name="$t('settings')"
        >
          <i class="headerDesktop_itemIcon -settings"></i>
          <div class="headerDesktop_itemName">
            {{ $t('Settings') }}
          </div>
          <span v-if="isIndicatorVisible" class="headerDesktop_indicator -item" />
        </router-link>

        <router-link
          :to="{ name: PAGE_NAME.LOGOUT }"
          class="headerDesktop_item"
          data-analytics
          data-analytics-element-type="menu_user"
          :data-analytics-element-target="{ name: PAGE_NAME.LOGOUT }"
          :data-analytics-element-id="PAGE_NAME.LOGOUT"
          :data-analytics-element-name="$t('logout')"
        >
          <i class="headerDesktop_itemIcon -logout"></i>
          <div class="headerMain_itemName">
            {{ $t('logout') }}
          </div>
        </router-link>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed } from 'vue'

import Token from '@web/common/token'
import { useStore } from '@web/store'

import PointsLabel from '@web/platform.v3/components/pointsLabel.vue'
import MenuTop from '@web/platform.v3/components/menu/top.vue'

import PAGE_NAME from '@web/consts/PAGE_NAME'
import Analytics from '@web/services/Analytics/Analytics'
import { specialProjectId } from '@web/common/ExternalConfig/getters'
import { useRemindersIndicator } from '@web/platform.v3/composables/useRemindersIndicator'

export default defineComponent({
  name: 'HeaderDesktop',
  components: {
    PointsLabel,
    MenuTop
  },
  setup () {
    const store = useStore()
    const menuOpen = ref(false)
    const projectId = computed(() => specialProjectId())
    const user = computed(() => store.getters['user/user'])
    const username = computed(() => store.getters['user/username'])
    const email = computed(() => user.value.email)
    const logo = computed(() => store.getters['assets/logo'])
    const menu = computed(() => store.getters['menu/setting'])
    const userScoreAmount = computed(() => store.getters['userScore/amount'])
    const urlAdmin = computed(() => `/admin/login?token=${Token.get()}`)
    const projectName = computed(() => store.getters['assets/projectTitle'])
    const isShowVideoConsultations = computed(() => store.getters['menu/getVideoConsultations'])
    const { isIndicatorVisible } = useRemindersIndicator()

    function closeMenu () {
      menuOpen.value = false
      document.body.removeEventListener('click', closeMenu)
      Analytics.send({
        action: 'close_burger',
        project: 'platform'
      })
    }

    function openMenu () {
      menuOpen.value = true
      document.body.addEventListener('click', closeMenu)
      Analytics.send({
        action: 'open_burger',
        project: 'platform'
      })
    }

    function toggleMenu () {
      if (menuOpen.value) {
        closeMenu()
      } else {
        openMenu()
      }
    }

    return {
      projectName,
      projectId,
      menuOpen,
      user,
      username,
      email,
      logo,
      menu,
      userScoreAmount,
      urlAdmin,
      isShowVideoConsultations,
      isIndicatorVisible,

      toggleMenu,
      PAGE_NAME
    }
  }
})
</script>

<style lang="scss">
@import '@web/assets/scss/textContents.scss';
.headerDesktop {
  display: flex;
  justify-content: space-between;
  align-items: center;
  min-width: 1217px;
  max-width: 1217px;
  margin: 0 auto 16px;
  padding: 24px 0 20px 18px;
  height: 90px;
  background-color: var(--ac-color-background);
  position: sticky;
  top: 0;
  z-index: 10;

  &_logo {
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center center;
    width: 138px;
    height: 30px;
    display: flex;
    align-items: center;
    justify-content: center;
    mask-position: center center;
    mask-size: contain;
    mask-repeat: no-repeat;

    &.-any {
      background-color: #A990E2;
      background-image: none !important;
    }

    &.-take {
      mask-image: none !important;
      width: 182px;
      height: 46px;
    }

    img {
      max-width: 100%;
      max-height: 100%;
    }
  }

  &_userBlock {
    position: relative;
    display: flex;
    align-items: center;
    gap: 4px;
    border-radius: 42px;
    padding-top: 4px;
    padding-right: 12px;
    padding-bottom: 4px;
    padding-left: 6px;
    background-color: #FFFFFF;
    cursor: pointer;
  }

  &_user {
    display: flex;
    flex-flow: column nowrap;
    align-items: flex-start;
    justify-content: flex-start;
    width: 170px;
    overflow: hidden;
  }

  &_userWrapper {
    position: relative;
    display: flex;
    align-items: center;
    margin-left: auto;
    border-radius: 6px;
    text-decoration: none;
  }

  &_indicator {
    width: 11px;
    height: 11px;
    border-radius: 50%;
    background: var(--ac-color-deep-liliac);
    border: 1.5px solid var(--ac-color-white);
    flex: 0 0 auto;

    &.-avatar {
      position: absolute;
      bottom: 0;
      right: 0;
    }

    &.-item {
      display: inline-block;
      margin-left: auto;
      margin-right: 4px;
    }
  }

  &_avatar {
    position: relative;
    display: flex;
    width: 34px;
    height: 34px;
    margin-right: 6px;
    background-color: var(--ac-color-liliac-secondary);
    border: 0.89px solid var(--ac-color-white);
    border-radius: 50%;
    // overflow: hidden;
  }

  &_photo {
    display: block;
    width: 100%;
    height: 100%;
    object-position: center;
    object-fit: cover;
    border-radius: 50%;
  }

  &_name {
    display: block;
    width: 100%;

    color: var(--ac-color-deep-navy-blue);
    white-space: pre;
    text-overflow: ellipsis;
    @extend .text-sm;
  }

  &_email {
    display: block;
    width: 100%;
    color: var(--ac-color-gray);
    @extend .text-xs;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  &_settingMenu {
    position: relative;
  }

  &_setting {
    position: relative;

    display: block;
    width: 20px;
    height: 20px;

    background-image: url(~@ui-kit/icons/icon-arrow-down-new.svg);
    background-repeat: no-repeat;
    background-size: cover;
    background-size: auto;
    background-position: center;
    flex: 0 0 auto;

    &.-open {
      transform: rotate(180deg);
    }
  }

  &_menu {
    position: absolute;
    top: calc(100% + 10px);
    right: 0;
    z-index: 99;

    width: 282px;
    border-radius: 22px;
    background-color: white;
    box-shadow: 0 4px 4px 0 rgba(41, 84, 251, .1);
    padding: 4px 12px;
  }

  &_item {
    position: relative;

    display: flex;
    flex-flow: row nowrap;
    align-items: center;
    justify-content: flex-start;
    color: var(--ac-color-black);
    @extend .text-sm;
    text-decoration: none;
    transition: all .3s;
    gap: 10px;
    padding-top: 12px;
    padding-bottom: 14px;
    border-bottom: 1px solid var(--ac-color-line);

    &:first-letter {
      text-transform: uppercase;
    }

    &:last-child {
      border-color: transparent;
    }

    &:hover {
      color: var(--ac-color-deep-liliac);
      @extend .text-sm-medium;
    }
  }

  &_itemIcon {
    width: 20px;
    height: 20px;
    background-repeat: no-repeat;
    background-size: contain;
    background-position: center;

    &.-course {
      background-image: url(~@web/assets/images/menu/icon-menu-mycourses.svg);
    }

    &.-chat {
      background-image: url(~@web/assets/images/menu/icon-menu-messages.svg);
    }

    &.-homework {
      background-image: url(~@web/assets/images/menu/icon-menu-homeworks.svg);
    }

    &.-userEdit {
      background-image: url(~@web/assets/images/menu/icon-menu-profile-edit-new.svg);
    }

    &.-settings {
      background-image: url(~@web/assets/images/menu/icon-menu-settings.svg);
    }

    &.-points {
      background-image: url(~@web/assets/images/menu/icon-menu-points-new.svg);
    }

    &.-link {
      background-image: url(~@web/assets/images/menu/icon-menu-linksocial.svg);
    }

    &.-stat {
      background-image: url(~@web/assets/images/icon-stat.svg);
    }

    &.-subscribe {
      background-image: url(~@web/assets/images/icon-subscribe.svg);
    }

    &.-referral {
      background-image: url(~@web/assets/images/icon-referral.svg);
    }

    &.-instruction {
      background-image: url(~@web/assets/images/icon-instruction.svg);
    }

    &.-support {
      background-image: url(~@web/assets/images/menu/icon-menu-support.svg);
    }

    &.-videoconsultations {
      background-image: url(~@web/assets/images/menu/icon-menu-videoconsultations.svg);
    }

    &.-password {
      background-image: url(~@web/assets/images/icon-password.svg);
    }

    &.-payment {
      background-image: url(~@web/assets/images/icon-payment.svg);
    }

    &.-logout,
    &.-exit {
      background-image: url(~@web/assets/images/menu/icon-menu-logout.svg);
    }

    &.-ext {
      background-image: url(~@web/assets/images/icon-ext.svg);
    }

    &.-manual {
      background-image: url(~@web/assets/images/icon-manual-blue.svg);
    }
  }
}
</style>
